import { useEffect, useState, startTransition } from "react";
import { useMedplum } from "@medplum/react";
import type {
  Communication,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";
import { getPractitionerDisplayName } from "@benchmark/core/resolvers";

/**
 * Result type for useCommunicationSenderName hook.
 * @agentic @benchmark/core
 */
export type UseCommunicationSenderNameResult = {
  /** Resolved sender name */
  senderName: string;
  /** Loading state */
  isLoading: boolean;
};

/**
 * Resolves sender name from Communication resource; handles PractitionerRole and Practitioner references.
 * @agentic @benchmark/core
 */
export function useCommunicationSenderName(
  communication: Communication | undefined,
): UseCommunicationSenderNameResult {
  const medplum = useMedplum();
  const [senderName, setSenderName] = useState<string>("Unknown Sender");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!communication?.sender) {
      startTransition(() => {
        setSenderName("Unknown Sender");
        setIsLoading(false);
      });
      return;
    }

    const sender = communication.sender;

    // If display is already set, use it
    if (sender.display) {
      const display = sender.display;
      startTransition(() => {
        setSenderName(display);
        setIsLoading(false);
      });
      return;
    }

    // If no reference, can't resolve
    if (!sender.reference) {
      startTransition(() => {
        setSenderName("Unknown Sender");
        setIsLoading(false);
      });
      return;
    }

    // Check if it's a PractitionerRole reference
    if (sender.reference.startsWith("PractitionerRole/")) {
      startTransition(() => {
        setIsLoading(true);
      });
      medplum
        .readReference(sender)
        .then((resource) => {
          if (resource?.resourceType === "PractitionerRole") {
            const practitionerRole = resource as PractitionerRole;
            // Resolve Practitioner from PractitionerRole
            if (practitionerRole.practitioner?.reference) {
              return medplum.readReference(practitionerRole.practitioner);
            }
          }
          return null;
        })
        .then((practitioner) => {
          if (practitioner?.resourceType === "Practitioner") {
            const name = getPractitionerDisplayName(
              practitioner as Practitioner,
            );
            startTransition(() => {
              setSenderName(name);
            });
          } else {
            startTransition(() => {
              setSenderName("Unknown Sender");
            });
          }
        })
        .catch(() => {
          startTransition(() => {
            setSenderName("Unknown Sender");
          });
        })
        .finally(() => {
          startTransition(() => {
            setIsLoading(false);
          });
        });
      return;
    }

    // Check if it's a Practitioner reference
    if (sender.reference.startsWith("Practitioner/")) {
      startTransition(() => {
        setIsLoading(true);
      });
      medplum
        .readReference(sender)
        .then((resource) => {
          if (resource?.resourceType === "Practitioner") {
            const name = getPractitionerDisplayName(resource as Practitioner);
            startTransition(() => {
              setSenderName(name);
            });
          } else {
            startTransition(() => {
              setSenderName("Unknown Sender");
            });
          }
        })
        .catch(() => {
          startTransition(() => {
            setSenderName("Unknown Sender");
          });
        })
        .finally(() => {
          startTransition(() => {
            setIsLoading(false);
          });
        });
      return;
    }

    // For other reference types, use display if available, otherwise fallback
    startTransition(() => {
      setSenderName(sender.display || "Unknown Sender");
      setIsLoading(false);
    });
  }, [communication?.sender, medplum]);

  return { senderName, isLoading };
}
